/**
 * Mon profil › Notifications (/profil/notifications, aussi par « Voir toutes les notifications » du dock et par le
 * menu Profil) : toutes mes notifications, plus récentes d'abord, filtre Toutes / Non lues (dans l'adresse de la
 * page : ?etat=non-lues), pages de 20 (« Afficher plus », curseur de l'API), « Tout marquer comme lu ».
 * Chaque ligne : icône du type, message avec lien vers l'objet notifiant (<LienPage> : clic = dans la page, Ctrl/⌘ +
 * clic ou icône = nouvelle page), auteur, filiale, date, état et « Marquer comme lue ». Ouvrir l'objet (lien ou clic
 * sur la ligne) marque la notification comme lue. Volet étroit (mobile, côte à côte) : filiale et date passent sous
 * le message.
 */
import React from 'react';
import { useSearchParams } from 'react-router-dom';
import type { Notification } from '@agora/shared';
import { Button, DataTable, Icon, SegmentedControl, StatusBadge, cx, zoomRacine } from '../../design';
import { ErreurApi, LienPage, useEtatPage, useFormats, useOuvrir, useT } from '../../socle';
import {
  iconeNotification, texteNotification, useActionsNotifications, useListeNotifications, type FiltreNotifications,
} from '../notifications';

/** Largeur du volet sous laquelle les colonnes Filiale et Date passent sous le message. */
const SEUIL_ETROIT = 720;

function useEtroit(ref: React.RefObject<HTMLElement | null>): boolean {
  const [etroit, setEtroit] = React.useState(false);
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Mesure immédiate (avant l'affichage), puis suivi des changements de largeur (fenêtre, côte à côte). Largeurs à
    // l'échelle de la page, comme celles de ResizeObserver (la mesure d'écran est divisée par le zoom de l'affichage).
    const mesurer = (largeur: number) => { if (largeur > 0) setEtroit(largeur < SEUIL_ETROIT); };
    mesurer(el.getBoundingClientRect().width / zoomRacine());
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([e]) => { if (e) mesurer(e.contentRect.width); });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return etroit;
}

/**
 * Lien vers l'objet notifiant (<LienPage>) ; toute ouverture marque la notification comme lue. Le Ctrl/⌘ + clic sur un
 * lien interne est pris en charge par l'espace de travail AVANT React (phase de capture) : la lecture est donc aussi
 * notée au relâchement du pointeur et à la touche Entrée (appels dédoublonnés par useActionsNotifications).
 */
function LienObjet({ to, label, lire }: { to: string; label: string; lire: () => void }) {
  return (
    <span
      className="agx-notif-lien"
      onPointerUp={(e) => { if (e.button === 0 || e.button === 1) lire(); }}
      onKeyDown={(e) => { if (e.key === 'Enter') lire(); }}
      onClickCapture={lire}
    >
      <LienPage to={to} label={label} />
    </span>
  );
}

export function Notifications() {
  const { t } = useT();
  const f = useFormats();
  const ouvrir = useOuvrir();
  const [params, setParams] = useSearchParams();
  const etat: FiltreNotifications = params.get('etat') === 'non-lues' ? 'non-lues' : 'toutes';
  const liste = useListeNotifications(etat);
  const { marquerLue, toutLire, toutEnCours, erreur } = useActionsNotifications();
  const conteneur = React.useRef<HTMLDivElement>(null);
  const etroit = useEtroit(conteneur);
  useEtatPage(etat === 'non-lues' ? t('notifications.filtreNonLues') : undefined);

  const elements = liste.data?.pages.flatMap((p) => p.elements) ?? [];
  const nonLues = liste.data?.pages[0]?.nonLues ?? 0;
  const code = (e: unknown) => (e instanceof ErreurApi ? e.code : 'interne');
  const filtrer = (v: string) => setParams((p) => {
    const n = new URLSearchParams(p);
    if (v === 'non-lues') n.set('etat', v); else n.delete('etat');
    return n;
  }, { replace: true });
  const ouvrirObjet = (n: Notification) => {
    marquerLue(n);
    if (n.lien) ouvrir(n.lien);
  };

  const C = (c: 'message' | 'filiale' | 'date' | 'etat') => t(`notifications.colonnes.${c}`);
  const lignes = elements.map((n) => {
    const lue = !!n.lueLe;
    const message = texteNotification(n, t);
    const quand = <time dateTime={n.creeLe} title={f.dateHeure(n.creeLe)}>{etroit ? f.relatif(n.creeLe) : f.dateHeure(n.creeLe)}</time>;
    return {
      id: n.id,
      message: (
        <span className={cx('agx-notif-cellule', !lue && 'agx-notif-nonlue')}>
          <span className="agx-notif-ico" aria-hidden="true"><Icon name={iconeNotification(n)} size={16} /></span>
          <span className="agx-notif-txt">
            {n.lien ? <LienObjet to={n.lien} label={message} lire={() => marquerLue(n)} /> : <span className="agx-notif-msg">{message}</span>}
            <span className="agx-notif-meta">
              <span>{n.auteur ?? t('notifications.systeme')}</span>
              {etroit && n.filiale ? <span className="ag-mono">{n.filiale}</span> : null}
              {etroit ? quand : null}
            </span>
          </span>
        </span>
      ),
      filiale: n.filiale ? <span className="ag-mono">{n.filiale}</span> : '—',
      date: quand,
      etat: (
        <span className="agx-notif-etat">
          <StatusBadge status={lue ? 'neutral' : 'info'} label={lue ? t('notifications.lue') : t('notifications.nonLue')} />
          {!lue ? (
            <Button
              variant="quiet" size="sm" icon="check" className="agx-notif-action"
              aria-label={etroit ? `${t('notifications.marquerLue')} : ${message}` : undefined} title={t('notifications.marquerLue')}
              onClick={(e) => { e.stopPropagation(); marquerLue(n); }}
            >
              {etroit ? null : t('notifications.marquerLue')}
            </Button>
          ) : null}
        </span>
      ),
    };
  });

  const colonnes = etroit
    ? [{ key: 'message', label: C('message') }, { key: 'etat', label: C('etat') }]
    : [
      { key: 'message', label: C('message') },
      { key: 'filiale', label: C('filiale'), width: 90 },
      { key: 'date', label: C('date'), width: 190 },
      { key: 'etat', label: C('etat'), width: 260 },
    ];

  return (
    <div ref={conteneur} className="agx-notifs-ecran">
      <div className="ag-pagehead">
        <div>
          <h1>{t('notifications.titre')}</h1>
          <p className="agx-sous-titre">{t('notifications.aide')}</p>
        </div>
        <div className="agx-actions">
          <Button variant="secondary" icon="check" disabled={!nonLues || toutEnCours} onClick={toutLire}>{t('notifications.toutLire')}</Button>
        </div>
      </div>
      {erreur ? (
        <div className="agx-message" role="alert"><Icon name="bell" size={16} /><span>{t(`erreurs.${code(erreur)}`)}</span></div>
      ) : null}
      <DataTable
        dense
        ariaLabel={t('notifications.titre')}
        toolbar={
          <div className="agx-notifs-outils">
            <SegmentedControl
              ariaLabel={t('notifications.titre')} value={etat} onChange={filtrer}
              options={[{ value: 'toutes', label: t('notifications.toutes') }, { value: 'non-lues', label: t('notifications.filtreNonLues') }]}
            />
            {liste.data ? <span className="agx-aide" role="status">{t('notifications.nonLues', { n: f.nombre(nonLues) })}</span> : null}
          </div>
        }
        columns={colonnes}
        rows={lignes}
        loading={liste.isLoading} loadingLabel={t('app.chargement')}
        emptyLabel={
          liste.error ? t(`erreurs.${code(liste.error)}`)
            : etat === 'non-lues' ? t('notifications.videNonLues') : t('notifications.vide')
        }
        // Ligne : ouvre l'objet dans la page (Ctrl/⌘ + clic : nouvelle page) et marque la notification comme lue.
        onRowClick={(id) => { const n = elements.find((x) => x.id === id); if (n) ouvrirObjet(n); }}
        footer={liste.hasNextPage ? (
          <Button variant="quiet" size="sm" onClick={() => void liste.fetchNextPage()} disabled={liste.isFetchingNextPage}>{t('notifications.plus')}</Button>
        ) : null}
      />
    </div>
  );
}
